/*
 * Gateway onboarding modal (TTI design handoff).
 * Extends the theme's ModalDialog. Runs the 4-step modal in one of two modes:
 *
 *  - intercept  (product page): catches the gateway add-to-cart submit BEFORE
 *               it happens (capture phase), shows the flow, and on Confirm
 *               replays the form to /cart/add (using the quantity chosen in the
 *               modal) then redirects to the cart. Skip/close still adds the
 *               item normally.
 *
 *  - standalone (e.g. homepage): opened from any [data-gw-ob-open] trigger.
 *               No form exists, so the modal resolves the variant itself from
 *               the chosen Frequency Band (+ the bundled subscription value) and
 *               the chosen quantity, then adds + redirects on Confirm. Closing
 *               adds nothing.
 *
 * The EUR/USD prices are display-only (subscription is billed externally).
 */
if (!customElements.get('gateway-onboarding-modal')) {
  customElements.define(
    'gateway-onboarding-modal',
    class GatewayOnboardingModal extends ModalDialog {
      constructor() {
        super();
        this.totalSteps = parseInt(this.dataset.totalSteps, 10) || 4;
        this.currentStep = 1;
        this.mode = 'intercept';
        this.added = false;
        this.completing = false;
        this.intercepting = false;
        this.qty = 1;

        this.dialog = this.querySelector('[role="dialog"]');
        this.steps = Array.from(this.querySelectorAll('[data-step]'));
        this.segs = Array.from(this.querySelectorAll('[data-dot]'));
        this.announce = this.querySelector('[data-step-announce]');
        this.errorEl = this.querySelector('.gw-ob__error');
        this.confirmButton = this.querySelector('[data-go="confirm"]');

        // Selection controls (band + quantity) live on the confirm step.
        this.bandPick = this.querySelector('[data-bandpick]');
        this.bandButtons = Array.from(this.querySelectorAll('.gw-ob__band'));
        this.qtyInput = this.querySelector('[data-qty-input]');

        // Summary hooks (rows toggled / values updated per mode).
        this.sumTitle = this.querySelector('[data-sum-title]');
        this.sumPrice = this.querySelector('[data-sum-price]');
        this.sumIncluded = this.querySelector('[data-sum-included]');
        this.sumSub = this.querySelector('[data-sum-sub]');
        this.sumQtyValue = this.querySelector('[data-sum-qty-value]');

        // Preserve the server-rendered (product-page) summary defaults.
        this.baseTitle = this.sumTitle ? this.sumTitle.textContent : '';
        this.basePrice = this.sumPrice ? this.sumPrice.textContent : '';

        // Default band selection = the pre-marked button (first one). `defaultBand`
        // stays fixed so each fresh standalone open resets to it, while
        // `selectedBand` tracks the shopper's current choice.
        this.defaultBand = this.bandButtons.find((b) => b.getAttribute('aria-pressed') === 'true') || this.bandButtons[0] || null;
        this.selectedBand = this.defaultBand;

        this.querySelectorAll('[data-go]').forEach((button) => {
          button.addEventListener('click', () => this.onNav(button.dataset.go));
        });

        this.bandButtons.forEach((button) => {
          button.addEventListener('click', () => this.selectBand(button));
        });

        this.querySelectorAll('[data-qty-step]').forEach((button) => {
          button.addEventListener('click', () => this.stepQty(parseInt(button.dataset.qtyStep, 10) || 0));
        });
        if (this.qtyInput) {
          this.qtyInput.addEventListener('change', () => this.setQty(this.qtyInput.value));
          this.qtyInput.addEventListener('input', () => this.setQty(this.qtyInput.value, true));
        }
      }

      connectedCallback() {
        super.connectedCallback(); // moves to <body>, sets this.dataset.section
        if (this.initialized) return;
        this.initialized = true;

        if (window.Shopify && Shopify.designMode) {
          document.addEventListener('shopify:section:select', (event) => {
            if (event.detail && event.detail.sectionId === this.dataset.section) this.openPreview();
          });
          document.addEventListener('shopify:section:deselect', (event) => {
            if (event.detail && event.detail.sectionId === this.dataset.section) super.hide();
          });
          return;
        }

        // intercept mode: capture phase -> runs before product-form.js's handler.
        document.addEventListener('submit', this.onCaptureSubmit.bind(this), true);
        // standalone mode: any [data-gw-ob-open] trigger (e.g. homepage button).
        document.addEventListener('click', this.onTriggerClick.bind(this));
      }

      onTriggerClick(event) {
        const trigger = event.target.closest('[data-gw-ob-open]');
        if (!trigger) return;
        event.preventDefault();
        this.openStandalone(trigger);
      }

      onCaptureSubmit(event) {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form.dataset.type !== 'add-to-cart-form') return;

        event.preventDefault();
        event.stopImmediatePropagation();

        this.mode = 'intercept';
        this.form = form;
        this.opener = form.querySelector('[type="submit"]') || document.activeElement;
        this.intercepting = true;
        this.added = false;
        this.completing = false;

        // Seed the modal quantity from the product form's quantity field.
        const formQty = parseInt(new FormData(form).get('quantity'), 10);
        this.setQty(Number.isFinite(formQty) && formQty > 0 ? formQty : 1);

        this.applyMode();
        this.clearError();
        this.renderStep(1);
        this.show(this.opener);
      }

      openStandalone(opener) {
        this.mode = 'standalone';
        this.form = null;
        this.intercepting = false;
        this.added = false;
        this.completing = false;

        // Reset selection to the default band + quantity 1.
        if (this.bandButtons.length) this.selectBand(this.defaultBand || this.bandButtons[0]);
        this.setQty(1);

        this.applyMode();
        this.clearError();
        this.setLoading(false);
        this.renderStep(1);
        this.show(opener || this.querySelector('.gw-ob__next'));
      }

      openPreview() {
        this.mode = 'standalone';
        this.form = null;
        this.intercepting = false;
        if (this.bandButtons.length) this.selectBand(this.defaultBand || this.bandButtons[0]);
        this.setQty(1);
        this.applyMode();
        this.clearError();
        this.renderStep(1);
        this.show(this.querySelector('.gw-ob__next'));
      }

      // Toggle band picker + summary rows to match the current mode.
      applyMode() {
        const standalone = this.mode === 'standalone';
        if (this.bandPick) this.bandPick.toggleAttribute('hidden', !standalone || !this.bandButtons.length);
        // Standalone bundles the subscription into the variant price, so show
        // "included" and hide the separate subscription line; intercept keeps
        // the product page's original subscription row.
        if (this.sumIncluded) this.sumIncluded.toggleAttribute('hidden', !standalone);
        if (this.sumSub) this.sumSub.toggleAttribute('hidden', standalone);

        if (standalone) {
          this.updateStandaloneSummary();
        } else if (this.sumTitle && this.sumPrice) {
          this.sumTitle.textContent = this.baseTitle;
          this.sumPrice.textContent = this.basePrice;
        }
        this.updateQtySummary();
      }

      selectBand(button) {
        if (!button) return;
        this.selectedBand = button;
        this.bandButtons.forEach((b) => {
          const active = b === button;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        if (this.mode === 'standalone') this.updateStandaloneSummary();
      }

      updateStandaloneSummary() {
        const band = this.selectedBand;
        if (this.sumTitle) {
          const bandName = band ? band.dataset.band : '';
          this.sumTitle.textContent = bandName ? `${this.baseTitle} — ${bandName}` : this.baseTitle;
        }
        if (this.sumPrice && band && band.dataset.price) this.sumPrice.textContent = band.dataset.price;
      }

      stepQty(delta) {
        this.setQty(this.qty + delta);
      }

      setQty(value, fromInput) {
        let n = parseInt(value, 10);
        if (!Number.isFinite(n) || n < 1) n = fromInput ? n : 1;
        if (Number.isFinite(n) && n >= 1) this.qty = n;
        // While typing, don't fight the user's caret; otherwise reflect the clamp.
        if (this.qtyInput && !fromInput) this.qtyInput.value = this.qty;
        this.updateQtySummary();
      }

      updateQtySummary() {
        if (this.sumQtyValue) this.sumQtyValue.textContent = this.qty;
      }

      onNav(action) {
        switch (action) {
          case 'next':
            this.renderStep(Math.min(this.currentStep + 1, this.totalSteps));
            break;
          case 'back':
            this.renderStep(Math.max(this.currentStep - 1, 1));
            break;
          case 'confirm':
            this.confirm();
            break;
          case 'skip':
            this.hide();
            break;
        }
      }

      renderStep(step) {
        this.currentStep = step;
        const isLast = step === this.totalSteps;

        this.steps.forEach((panel) => {
          const active = parseInt(panel.dataset.step, 10) === step;
          panel.toggleAttribute('hidden', !active);
          panel.toggleAttribute('inert', !active);
        });

        this.segs.forEach((seg) => {
          const n = parseInt(seg.dataset.dot, 10);
          seg.classList.toggle('is-active', n === step);
          seg.classList.toggle('is-complete', n < step);
        });

        this.toggle('.gw-ob__back', step > 1);
        // Skip only makes sense in intercept mode (there's a pending add to skip to).
        this.toggle('.gw-ob__skip', !isLast && this.mode === 'intercept');
        this.toggle('.gw-ob__next', !isLast);
        this.toggle('.gw-ob__confirm', isLast);

        const activePanel = this.steps.find((p) => parseInt(p.dataset.step, 10) === step);
        const heading = activePanel && activePanel.querySelector('.gw-ob__h2');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          trapFocus(this, heading);
        } else {
          trapFocus(this, this.dialog);
        }

        if (this.announce) {
          const stepWord = this.dataset.stepWord || 'Step';
          const ofWord = this.dataset.ofWord || 'of';
          this.announce.textContent = `${stepWord} ${step} ${ofWord} ${this.totalSteps}`;
        }
      }

      toggle(selector, show) {
        const el = this.querySelector(selector);
        if (el) el.toggleAttribute('hidden', !show);
      }

      confirm() {
        if (this.added || this.completing) return;

        let variantId = null;
        if (this.mode === 'standalone') {
          variantId = (this.selectedBand && this.selectedBand.dataset.variantId) || this.dataset.defaultVariantId;
          if (!variantId) {
            this.showError('This product is currently unavailable.');
            return;
          }
        } else if (!this.form) {
          super.hide();
          return;
        }

        this.completing = true;
        this.setLoading(true);
        this.addToCart(true, variantId).catch(() => {
          this.completing = false;
          this.setLoading(false);
        });
      }

      hide() {
        // Only the product-page intercept adds on close (the shopper already
        // clicked add-to-cart). Standalone close = abandon, add nothing.
        if (this.mode === 'intercept' && this.intercepting && this.form && !this.added && !this.completing) {
          this.addToCart(false).catch(() => {});
        }
        this.intercepting = false;
        super.hide();
      }

      // Builds the cart payload for the active mode and posts it to /cart/add.
      addToCart(redirect, variantId) {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        let formData;

        if (this.mode === 'standalone') {
          formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', this.qty);
        } else {
          formData = new FormData(this.form);
          formData.set('quantity', this.qty); // honour the quantity chosen in the modal
        }

        if (cart) {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message);
              throw new Error(response.description || response.message);
            }
            this.added = true;
            if (redirect) {
              window.location = this.dataset.redirectUrl || window.routes.cart_url;
              return;
            }
            if (cart) cart.renderContents(response);
            this.intercepting = false;
            super.hide();
          });
      }

      setLoading(loading) {
        if (!this.confirmButton) return;
        this.confirmButton.classList.toggle('loading', loading);
        this.confirmButton.setAttribute('aria-disabled', loading ? 'true' : 'false');
        const spinner = this.confirmButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !loading);
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || '';
        this.errorEl.toggleAttribute('hidden', !message);
      }

      clearError() {
        this.showError('');
      }
    }
  );
}
